import { useState } from "react";
import {
  prospectSchemaFields,
  mapperTypes,
  initialFormValues,
  initialAgeRanges,
  initialAjvSchemas,
  initialArrayDefaults,
  initialAddDefaultModal,
  initialEnrichmentModal,
  initialModifierModal,
  initialCommPrefModal,
  initialConsentModal,
  initialPreviewModal,
  initialSearchModal,
  initialExportStatus,
} from "../constants";

/**
 * Main state management hook for BatchConfigV2
 */
export const useBatchConfigState = () => {
  // Step management
  const [activeStep, setActiveStep] = useState(0);

  // File and data management
  const [filePreview, setFilePreview] = useState(null);
  const [fileMetadata, setFileMetadata] = useState(null);

  // Configuration form values
  const [formValues, setFormValues] = useState(initialFormValues);
  const [ageRanges, setAgeRanges] = useState(initialAgeRanges);
  const [selectedRange, setSelectedRange] = useState("default");
  const [newCountryCode, setNewCountryCode] = useState("");

  // Schema management
  const [ajvSchemas, setAjvSchemas] = useState(initialAjvSchemas);
  const [selectedSchemaMarket, setSelectedSchemaMarket] = useState("default");
  const [selectedAttribute, setSelectedAttribute] = useState("");
  const [newSchemaCode, setNewSchemaCode] = useState("");
  const [enumInputValue, setEnumInputValue] = useState("");

  // Mapping state
  const initialMappingDraft = prospectSchemaFields.reduce(
    (acc, field) => ({
      ...acc,
      [field]: {
        path: "",
        include: "",
        includeType: "none",
        transform: "None",
        script: "",
        includeScript: "",
      },
    }),
    {}
  );

  const [mappingDrafts, setMappingDrafts] = useState(
    mapperTypes.reduce((acc, type) => ({ ...acc, [type]: initialMappingDraft }), {})
  );

  const [mappers, setMappers] = useState(
    mapperTypes.reduce((acc, type) => ({ ...acc, [type]: {} }), {})
  );

  // Ingestion mapping state
  const [ingestionMappings, setIngestionMappings] = useState({});

  // Prospect mapping state
  const [prospectMappings, setProspectMappings] = useState([]);
  const [availableIngestionFields, setAvailableIngestionFields] = useState([]);

  // Enrich consumer mapping state
  // Default mapping: kocid -> kocid
  const [enrichConsumerMappings, setEnrichConsumerMappings] = useState([
    {
      prospectField: "kocid",
      ingestionField: "kocid",
      isArray: false,
      path: "kocid",
      include: "(e) => e",
    },
  ]);
  const [availableIngestionFieldsEnrich, setAvailableIngestionFieldsEnrich] = useState([]);

  // Array defaults state
  const [arrayDefaults, setArrayDefaults] = useState(initialArrayDefaults);

  // Modal states
  const [addDefaultModal, setAddDefaultModal] = useState(initialAddDefaultModal);
  const [enrichmentModal, setEnrichmentModal] = useState(initialEnrichmentModal);
  const [modifierModal, setModifierModal] = useState(initialModifierModal);
  const [commPrefModal, setCommPrefModal] = useState(initialCommPrefModal);
  const [consentModal, setConsentModal] = useState(initialConsentModal);
  const [previewModal, setPreviewModal] = useState(initialPreviewModal);
  const [searchModal, setSearchModal] = useState(initialSearchModal);

  // Export status
  const [exportStatus, setExportStatus] = useState(initialExportStatus);

  return {
    // Step management
    activeStep,
    setActiveStep,

    // File and data management
    filePreview,
    setFilePreview,
    fileMetadata,
    setFileMetadata,

    // Configuration form values
    formValues,
    setFormValues,
    ageRanges,
    setAgeRanges,
    selectedRange,
    setSelectedRange,
    newCountryCode,
    setNewCountryCode,

    // Schema management
    ajvSchemas,
    setAjvSchemas,
    selectedSchemaMarket,
    setSelectedSchemaMarket,
    selectedAttribute,
    setSelectedAttribute,
    newSchemaCode,
    setNewSchemaCode,
    enumInputValue,
    setEnumInputValue,

    // Mapping state
    mappingDrafts,
    setMappingDrafts,
    mappers,
    setMappers,
    ingestionMappings,
    setIngestionMappings,
    prospectMappings,
    setProspectMappings,
    availableIngestionFields,
    setAvailableIngestionFields,
    enrichConsumerMappings,
    setEnrichConsumerMappings,
    availableIngestionFieldsEnrich,
    setAvailableIngestionFieldsEnrich,
    arrayDefaults,
    setArrayDefaults,

    // Modal states
    addDefaultModal,
    setAddDefaultModal,
    enrichmentModal,
    setEnrichmentModal,
    modifierModal,
    setModifierModal,
    commPrefModal,
    setCommPrefModal,
    consentModal,
    setConsentModal,
    previewModal,
    setPreviewModal,
    searchModal,
    setSearchModal,

    // Export status
    exportStatus,
    setExportStatus,
  };
};

/**
 * Hook for managing stepper navigation
 */
export const useStepperNavigation = (activeStep, setActiveStep, totalSteps) => {
  const handleNext = () => setActiveStep((prev) => Math.min(prev + 1, totalSteps - 1));
  const handleBack = () => setActiveStep((prev) => Math.max(prev - 1, 0));
  const goToStep = (step) => setActiveStep(Math.max(0, Math.min(step, totalSteps - 1)));

  return {
    handleNext,
    handleBack,
    goToStep,
    canGoNext: activeStep < totalSteps - 1,
    canGoBack: activeStep > 0,
  };
};

/**
 * Hook for managing form values
 */
export const useFormManager = (formValues, setFormValues) => {
  const handleFormValueChange = (field, value) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  const updateFormValues = (newValues) => {
    setFormValues((prev) => ({ ...prev, ...newValues }));
  };

  const resetFormValues = () => {
    setFormValues(initialFormValues);
  };

  return {
    handleFormValueChange,
    updateFormValues,
    resetFormValues,
  };
};
